import { useState, useRef, useCallback } from 'react';
import { useBoardStore } from '../../stores/boardStore';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { MarkdownEditor } from './MarkdownEditor';
import { api } from '../../api/client';

interface CreateCardModalProps {
  onClose: () => void;
  defaultColumnId?: string;
}

// Supported file types
const MARKDOWN_EXTENSIONS = ['.md', '.markdown'];
const DOCUMENT_EXTENSIONS = ['.pdf', '.docx', '.doc', '.xlsx', '.xls', '.csv'];
const ALL_EXTENSIONS = [...MARKDOWN_EXTENSIONS, ...DOCUMENT_EXTENSIONS];

const FILE_TYPE_INFO: Record<string, { icon: string; label: string }> = {
  md: { icon: '📝', label: 'Markdown' },
  markdown: { icon: '📝', label: 'Markdown' },
  pdf: { icon: '📄', label: 'PDF' },
  docx: { icon: '📃', label: 'Word Document' },
  doc: { icon: '📃', label: 'Word Document' },
  xlsx: { icon: '📊', label: 'Excel Spreadsheet' },
  xls: { icon: '📊', label: 'Excel Spreadsheet' },
  csv: { icon: '📋', label: 'CSV Spreadsheet' },
};

function getFileExtension(filename: string): string {
  const match = filename.match(/\.([^.]+)$/);
  return match ? match[1].toLowerCase() : '';
}

export function CreateCardModal({ onClose, defaultColumnId }: CreateCardModalProps) {
  const { board, createCard, refreshBoard } = useBoardStore();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [content, setContent] = useState('');
  const [columnId, setColumnId] = useState(defaultColumnId || board?.columns[0]?.id || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [mode, setMode] = useState<'create' | 'upload'>('create');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileType, setFileType] = useState<'markdown' | 'attachment' | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !columnId) return;

    setIsSubmitting(true);
    setError('');

    try {
      // Create the card first
      const newCard = await createCard({
        title: title.trim(),
        description: description.trim() || undefined,
        column_id: columnId,
        content: content || `# ${title.trim()}\n\nStart writing here...`,
      });

      // If there's a non-markdown file, upload it as attachment
      if (selectedFile && fileType === 'attachment') {
        try {
          await api.uploadAttachment(newCard.id, selectedFile);
        } catch (uploadErr) {
          console.error('Failed to upload attachment:', uploadErr);
          // Don't fail the whole operation, card is created
        }
      }

      await refreshBoard();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileSelect = useCallback((file: File) => {
    const ext = getFileExtension(file.name);
    const isMarkdown = MARKDOWN_EXTENSIONS.some(e => file.name.toLowerCase().endsWith(e));
    const isDocument = DOCUMENT_EXTENSIONS.some(e => file.name.toLowerCase().endsWith(e));

    if (!isMarkdown && !isDocument) {
      setError(`Unsupported file type. Supported: ${ALL_EXTENSIONS.join(', ')}`);
      return;
    }

    setSelectedFile(file);
    setError('');

    if (isMarkdown) {
      // Read markdown content
      setFileType('markdown');
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        setContent(text);

        // Extract title from filename or first heading
        const filenameTitle = file.name.replace(/\.(md|markdown)$/i, '').replace(/[-_]/g, ' ');
        const headingMatch = text.match(/^#\s+(.+)$/m);
        setTitle(headingMatch ? headingMatch[1] : filenameTitle);

        // Extract description from first paragraph
        const paragraphMatch = text.match(/^(?!#)(.+)$/m);
        if (paragraphMatch) {
          setDescription(paragraphMatch[1].slice(0, 150));
        }

        setMode('create');
      };
      reader.onerror = () => {
        setError('Failed to read file');
      };
      reader.readAsText(file);
    } else {
      // For documents, just prepare to attach
      setFileType('attachment');
      const filenameTitle = file.name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' ');
      setTitle(filenameTitle);
      setDescription(`${FILE_TYPE_INFO[ext]?.label || 'Document'} attachment`);
      setContent(`# ${filenameTitle}\n\nThis document has an attached ${FILE_TYPE_INFO[ext]?.label || 'file'}.`);
      setMode('create');
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  }, [handleFileSelect]);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const clearSelectedFile = () => {
    setSelectedFile(null);
    setFileType(null);
    setTitle('');
    setDescription('');
    setContent('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} size="xl">
      <form onSubmit={handleSubmit} className="flex flex-col max-h-[90vh]">
        {/* Header - Fixed */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-200 shrink-0">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">New Document</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content - Scrollable */}
        <div
          className={`flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 ${isDragging ? 'bg-blue-50' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {error && (
            <div className="p-3 bg-red-50 text-red-600 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Mode tabs */}
          <div className="flex border-b border-gray-200 -mx-4 sm:-mx-6 px-4 sm:px-6">
            <button
              type="button"
              onClick={() => setMode('create')}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${mode === 'create'
                ? 'text-blue-600 border-blue-600'
                : 'text-gray-500 border-transparent hover:text-gray-700'
                }`}
            >
              Write New
            </button>
            <button
              type="button"
              onClick={() => setMode('upload')}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${mode === 'upload'
                ? 'text-blue-600 border-blue-600'
                : 'text-gray-500 border-transparent hover:text-gray-700'
                }`}
            >
              Upload File
            </button>
          </div>

          {mode === 'upload' ? (
            /* Upload Zone */
            <>
              {selectedFile ? (
                /* File selected preview */
                <div className="border-2 border-green-300 bg-green-50 rounded-xl p-6 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                    <span className="text-3xl">
                      {FILE_TYPE_INFO[getFileExtension(selectedFile.name)]?.icon || '📄'}
                    </span>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-1">
                    {selectedFile.name}
                  </h3>
                  <p className="text-sm text-gray-500 mb-4">
                    {(selectedFile.size / 1024).toFixed(1)} KB · {FILE_TYPE_INFO[getFileExtension(selectedFile.name)]?.label || 'Unknown'}
                  </p>
                  <div className="flex justify-center gap-2">
                    <button
                      type="button"
                      onClick={clearSelectedFile}
                      className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                    >
                      Choose Different File
                    </button>
                    <button
                      type="button"
                      onClick={() => setMode('create')}
                      className="px-4 py-2 text-sm text-white bg-green-600 hover:bg-green-700 rounded-lg"
                    >
                      Continue to Details
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-xl p-8 sm:p-12 text-center cursor-pointer transition-all ${isDragging
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                    }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ALL_EXTENSIONS.join(',')}
                    onChange={handleFileInputChange}
                    className="hidden"
                  />
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
                    <svg className="w-8 h-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-1">
                    Drop your file here
                  </h3>
                  <p className="text-sm text-gray-500 mb-4">
                    or click to browse your files
                  </p>
                  <div className="flex flex-wrap justify-center gap-2 text-xs text-gray-400">
                    <span className="px-2 py-1 bg-gray-100 rounded">📝 Markdown</span>
                    <span className="px-2 py-1 bg-gray-100 rounded">📄 PDF</span>
                    <span className="px-2 py-1 bg-gray-100 rounded">📃 Word</span>
                    <span className="px-2 py-1 bg-gray-100 rounded">📊 Excel</span>
                    <span className="px-2 py-1 bg-gray-100 rounded">📋 CSV</span>
                  </div>
                </div>
              )}
            </>
          ) : (
            /* Create form */
            <>
              {/* Selected file indicator */}
              {selectedFile && fileType === 'attachment' && (
                <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <span className="text-2xl">
                    {FILE_TYPE_INFO[getFileExtension(selectedFile.name)]?.icon || '📄'}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{selectedFile.name}</p>
                    <p className="text-xs text-gray-500">Will be attached to this card</p>
                  </div>
                  <button
                    type="button"
                    onClick={clearSelectedFile}
                    className="text-gray-400 hover:text-red-500 p-1"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Document title"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                  autoFocus
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (optional)
                </label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Brief summary of the document"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                />
              </div>

              {/* Column */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Column
                </label>
                <select
                  value={columnId}
                  onChange={(e) => setColumnId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none bg-white"
                >
                  {board?.columns.map((col) => (
                    <option key={col.id} value={col.id}>
                      {col.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Content - only show if not uploading a document attachment */}
              {fileType !== 'attachment' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Content
                  </label>
                  <MarkdownEditor
                    content={content}
                    onChange={setContent}
                  />
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer - Fixed */}
        <div className="flex flex-col sm:flex-row justify-end gap-2 px-4 sm:px-6 py-4 border-t border-gray-200 bg-gray-50 shrink-0">
          <Button type="button" variant="ghost" onClick={onClose} className="w-full sm:w-auto">
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={!title.trim() || isSubmitting}
            className="w-full sm:w-auto"
          >
            {isSubmitting ? 'Creating...' : selectedFile && fileType === 'attachment' ? 'Create with Attachment' : 'Create Document'}
          </Button>
        </div>

        {/* Drag hint overlay */}
        {isDragging && (
          <div className="absolute inset-0 bg-blue-500/10 pointer-events-none flex items-center justify-center z-10 rounded-xl">
            <div className="bg-white rounded-xl shadow-2xl p-8 text-center border-2 border-blue-500 border-dashed">
              <svg className="w-12 h-12 text-blue-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <p className="text-lg font-medium text-gray-900">Drop to upload</p>
            </div>
          </div>
        )}
      </form>
    </Modal>
  );
}
